import { DemDetType, Route } from 'features/BookingFlow'
import { Address, isAddress, Port, PortOrAddress } from 'features/Directory'
import { ISO } from 'service/ISO'

import { TTransportOrCarriageMode } from '../domain/TransportMode'
import { EventCode, IShipTpEvent } from '../domain/TransportPlanEvent'
import { ITimestampJSON } from '../domain/TransportPlanTimestamp'
import { TTransportType } from '../domain/TransportType'

type CarriagePlanId = string

interface Locatable {
  location: Location
}

interface Depot extends Locatable {
  _id?: string
  address: Address
  code: string
  countryCode: string
  name: string
  types: string[]
  details?: string
}

export declare type TpPoint = Address | Depot | Port

export type Trackable = {
  trackableId: string
  correlation: {
    containerNumber?: string
  }
}

export type TDelayType = -1 | 1 | 0
export const getTimestampDelayType = (delay: number): { delayType: TDelayType; isZero: boolean } => {
  const delayType = typeof delay === 'number' && delay >= 1 ? 1 : delay <= -1 ? -1 : 0
  const isZero = delayType === 0
  return {
    delayType,
    isZero,
  }
}

export interface IMilestone {}
interface IMilestoneCutoffs {
  vgm: string // date
  shippingInstructions: string // date
  containerYard: string // date
}
interface IMilestoneDepartureAirport {
  flightNumber?: string
}

interface IDemurrageDetentionDaysForType {
  freeDays: number
}
interface IMilestoneDemurrageDetention {
  manualInformationPerType: Record<DemDetType, string>
  portStorageIncluded: boolean
  demurrageDetentionDaysPerType: Record<DemDetType, IDemurrageDetentionDaysForType>
}

export interface IMilestonePickup extends IMilestone {
  name?: string
  address?: Address
  reference?: string
}

export interface IMilestoneDelivery extends IMilestone {
  name?: string
  address?: Address
  reference?: string
}

export type IMilestoneDpr = {
  port?: {
    name: string
    timezone: string
    unLocCode: string
    isPartnerPort: string
  }
}

export interface IMilestones extends Record<string, IMilestone | undefined> {
  pickup?: IMilestonePickup
  demurrageDetentionOrigin?: IMilestoneDemurrageDetention
  demurrageDetentionDestination?: IMilestoneDemurrageDetention
  pol?: IMilestone
  pod?: IMilestone
  departureAirport?: IMilestoneDepartureAirport
  destinationAirport?: IMilestone
  delivery?: IMilestoneDelivery
  dpr?: IMilestoneDpr
  order?: ('vgm' | 'customs')[]
  cutoffs?: IMilestoneCutoffs
}

export interface ICarriagePlan {
  id: CarriagePlanId
  route: Route
  trackables: Trackable[]
  events: Partial<Record<EventCode, IShipTpEvent>>
  milestones: IMilestones
  eventOrder: EventCode[]
}

export interface ICargoProcess {
  id: CarriagePlanId
  trackables: Trackable[]
  events: Partial<Record<EventCode, IShipTpEvent>>
  eventOrder: EventCode[]
}

export interface ITransportPlan2 {
  shipmentId: string
  transportType: TTransportType
  transportMode: TTransportOrCarriageMode
  origin?: ICargoProcess[]
  pre?: ICarriagePlan[]
  main?: ICarriagePlan[]
  on?: ICarriagePlan[]
  incoTerm?: ISO.IncoTermsCode
  // should it always be there?
  serviceCompletion: {
    // general shipment service completion date (the latest one from data array)
    completion?: string
    estimated?: ITimestampJSON
    initial?: ITimestampJSON
  }
}

export class TransportPlanExtractor {
  getPreCarriage(transportPlan: ITransportPlan2): ICarriagePlan[] {
    // @ts-expect-error TS(2322) FIXME: Type 'ICarriagePlan[] | undefined' is not assignab... Remove this comment to see the full error message
    return transportPlan && transportPlan.pre
  }

  getOnCarriage(transportPlan: ITransportPlan2): ICarriagePlan[] {
    // @ts-expect-error TS(2322) FIXME: Type 'ICarriagePlan[] | undefined' is not assignab... Remove this comment to see the full error message
    return transportPlan && transportPlan.on
  }

  getMainCarriage(transportPlan: ITransportPlan2): ICarriagePlan[] {
    // @ts-expect-error TS(2322) FIXME: Type 'ICarriagePlan[] | undefined' is not assignab... Remove this comment to see the full error message
    return transportPlan && transportPlan.main
  }

  getPickupMilestones(transportPlan: ITransportPlan2): IMilestonePickup[] {
    const preCarriage = this.getPreCarriage(transportPlan)

    if (preCarriage) {
      // @ts-expect-error TS(2322) FIXME: Type '(IMilestonePickup | undefined)[]' is not ass... Remove this comment to see the full error message
      return preCarriage.map(({ milestones }: ICarriagePlan) => milestones.pickup)
    }

    return []
  }

  getDeliveryMilestones(transportPlan: ITransportPlan2): IMilestoneDelivery[] {
    if (!transportPlan) {
      return []
    }

    const onCarriage = this.getOnCarriage(transportPlan)

    if (onCarriage) {
      // @ts-expect-error TS(2322) FIXME: Type '(IMilestoneDelivery | undefined)[]' is not a... Remove this comment to see the full error message
      return onCarriage.map(({ milestones }: ICarriagePlan) => milestones.delivery)
    }

    return []
  }

  getOriginAddresses(transportPlan: ITransportPlan2): PortOrAddress[] {
    if (!transportPlan) {
      return []
    }

    const preCarriage = this.getPreCarriage(transportPlan)
    const pickupAddresses = this.getPickupMilestones(transportPlan).map(this.getAddressFromMilestone)

    const originAddresses = this.getMainCarriage(transportPlan).map(this.getJourneyAddress('origin')).filter(Boolean)

    // @ts-expect-error TS(2322) FIXME: Type 'PortOrAddress[] | (Address | undefined)[]' i... Remove this comment to see the full error message
    return preCarriage ? pickupAddresses : originAddresses
  }

  getDestinationAddresses(transportPlan: ITransportPlan2): PortOrAddress[] {
    if (!transportPlan) {
      return []
    }

    const onCarriage = this.getOnCarriage(transportPlan)
    const deliveryAddresses = this.getDeliveryMilestones(transportPlan).map(this.getAddressFromMilestone)

    const destinationAddresses = this.getMainCarriage(transportPlan)
      .map(this.getJourneyAddress('destination'))
      .filter(Boolean)

    // @ts-expect-error TS(2322) FIXME: Type 'PortOrAddress[] | (Address | undefined)[]' i... Remove this comment to see the full error message
    return onCarriage ? deliveryAddresses : destinationAddresses
  }

  getJourneyAddress(journeyName: string) {
    return (carriagePlan: ICarriagePlan): PortOrAddress => {
      const { route } = carriagePlan
      return route && route.journey && route.journey[journeyName]
    }
  }

  getAddressFromMilestone(milestone: IMilestonePickup | IMilestoneDelivery) {
    return milestone.address
  }
}

export const tpPointToStr = (point: TpPoint): string => {
  if (!point) {
    // @ts-expect-error TS(2322) FIXME: Type 'undefined' is not assignable to type 'string... Remove this comment to see the full error message
    return
  }

  if (isAddress(point)) {
    return [point.postalCode, point.city].join(', ')
  }

  return point.name
}
